import {
  Budget,
  getBudgetCurrentStatus,
  VersionTotals,
} from "../types/budget/budget";
import { BudgetSummary } from "../types/budget/budget-summary";
import type { BudgetServiceLine } from "../types/budget/budget-service-v2";
import type { ServiceData } from "../types/budget/service-item";

/**
 * Detail payload from v1 full GET, v2 shell (`GET /v2/budget/:id`), or a partial
 * document while services are loaded separately.
 */
export type BudgetDetailInput = Partial<Omit<Budget, "_id">> & {
  _id?: string;
};

/**
 * v2 shell / header without `budgetServices` (`GET /v2/budget/:id`).
 * Used by {@link mergeShellWithServices} (FE-P3-00 / FE-P3-01).
 */
export type BudgetShellInput = Omit<Partial<Budget>, "budgetServices"> & {
  _id: string;
};

type ServiceLineLike =
  | ServiceData
  | BudgetServiceLine
  | (ServiceData & {
      sortOrder?: number;
      budgetId?: string;
      projectId?: string;
      lineItemKey?: string;
      updatedAt?: string | Date | null;
      updatedBy?: string | null;
      createdAt?: string | Date | null;
      __v?: number;
    });

const EMPTY_VERSION_TOTALS = {
  byCalculationType: { budgetTotals: {} },
  byItemType: { SUM: { totalInBRL: 0, totalInCurrency: 0 } },
} as VersionTotals;

function budgetDefaultsFromDetail(detail: BudgetDetailInput): Budget {
  return {
    project: detail.project ?? "",
    _id: detail._id,
    generalInfo: detail.generalInfo ?? [],
    status: detail.status,
    name: detail.name,
    type: detail.type,
    budgetServices: detail.budgetServices ?? [],
    itemTypeList: detail.itemTypeList ?? ({} as Budget["itemTypeList"]),
    addedItemTypeList: detail.addedItemTypeList ?? [],
    supplierList: detail.supplierList ?? [],
    // Este adaptador reconstrói o orçamento CAMPO A CAMPO: o que não estiver
    // listado aqui não chega à tela, mesmo vindo da API. Sem esta linha o
    // agrupamento era gravado corretamente e sumia no F5 — as linhas mantinham
    // o `groupId` (que vem dentro de `addedItemTypeList`) e viravam órfãs, indo
    // parar em "Sem agrupamento".
    budgetGroups: detail.budgetGroups ?? [],
    budgetValuesTotal: detail.budgetValuesTotal ?? EMPTY_VERSION_TOTALS,
    paymentSolicitation: detail.paymentSolicitation ?? [],
    files: detail.files ?? [],
    negociationValuesTotal: detail.negociationValuesTotal ?? [],
    closingValuesTotal: detail.closingValuesTotal ?? EMPTY_VERSION_TOTALS,
    isApiUpdate: detail.isApiUpdate,
    negotiationVersion: detail.negotiationVersion ?? 0,
    versionHistory: detail.versionHistory ?? [],
    budgetIndex: detail.budgetIndex ?? 0,
    baseValues: detail.baseValues ?? [],
    mainAppliedTaxes: detail.mainAppliedTaxes ?? [],
    budgetWideAppliedTaxes: detail.budgetWideAppliedTaxes ?? [],
    // Mesmo caso do agrupamento: o resumo de nota grava `noteAdvances` no
    // documento, mas este reconstrutor campo a campo descartava o array. A
    // navegação lia o estado local; o F5 montava o orçamento de novo e os
    // adiantamentos voltavam a zero.
    noteAdvances: detail.noteAdvances ?? [],
  };
}

function pickSummaryOverlay(summary: BudgetSummary) {
  return {
    _id: summary._id,
    project: summary.project,
    name: summary.name,
    status: summary.status,
    type: summary.type,
    budgetIndex: summary.budgetIndex,
    negotiationVersion: summary.negotiationVersion,
    versionHistory: summary.versionHistory,
    budgetValuesTotal: summary.budgetValuesTotal,
    negociationValuesTotal: summary.negociationValuesTotal,
    closingValuesTotal: summary.closingValuesTotal,
  };
}

/**
 * Projects a v2 collection line (or mixed payload) into editor {@link ServiceData}.
 *
 * Allowlist only (mirrors BE assembly): `_id`, `type`, `calcOption`, `supplierId`,
 * `supplierName`, `versions`, `budgetValues`, `negotiationValue`, `closingValues`.
 * Sparse `negotiationValue` holes are preserved as-is (no `null` → `undefined`).
 */
export function toServiceData(line: ServiceLineLike): ServiceData {
  return {
    _id: line._id,
    type: line.type,
    calcOption: line.calcOption,
    supplierId: line.supplierId,
    supplierName: line.supplierName ?? "",
    // Mesmo cuidado do `budgetDefaultsFromDetail`: este adaptador reconstrói o
    // serviço campo a campo. `groupId` é o que diz a qual agrupamento o serviço
    // pertence — sem ele aqui, todo serviço volta do servidor "sem agrupamento"
    // e as linhas aparecem zeradas.
    //
    // `null` vem da coleção `budget_services` (default do schema) e significa a
    // mesma coisa que ausente.
    groupId: (line as { groupId?: string | null }).groupId ?? undefined,
    versions: line.versions ?? [0],
    budgetValues: line.budgetValues,
    negotiationValue: line.negotiationValue ?? [],
    closingValues: line.closingValues,
  };
}

function serviceSortOrder(line: ServiceLineLike): number {
  return typeof (line as BudgetServiceLine).sortOrder === "number"
    ? (line as BudgetServiceLine).sortOrder
    : 0;
}

/**
 * Stable service order matching BE assembly: `sortOrder` ascending, then `_id`.
 */
export function sortServicesForEditor(
  services: ServiceLineLike[]
): ServiceLineLike[] {
  return [...services].sort((a, b) => {
    const orderDiff = serviceSortOrder(a) - serviceSortOrder(b);
    if (orderDiff !== 0) return orderDiff;
    return String(a._id ?? "").localeCompare(String(b._id ?? ""));
  });
}

/**
 * Merges a v2 budget shell with service lines into a full editor {@link Budget}.
 *
 * Strips collection-only fields from each line via {@link toServiceData}.
 * Callers should pass the fully drained service list (all pages) before
 * marking the editor loaded (FE-P3-01 / `fetchBudgetDetail` in queries/budget).
 */
export function mergeShellWithServices(
  shell: BudgetShellInput,
  services: ServiceLineLike[]
): Budget {
  const budgetServices = sortServicesForEditor(services).map(toServiceData);
  const base = budgetDefaultsFromDetail(shell);

  return {
    ...base,
    _id: shell._id,
    budgetServices,
  };
}

/**
 * Merges a v2 list row with a detail/shell payload into a full {@link Budget}
 * for editor/reducer compatibility during the list/detail split migration.
 *
 * List/summary fields (identity, version metadata, totals) win over detail so a
 * patched summary cache stays authoritative after autosave. Editor-only fields
 * (`budgetServices`, tax config, files, etc.) always come from `detail`.
 */
export function mergeSummaryWithDetail(
  summary: BudgetSummary,
  detail: BudgetDetailInput
): Budget {
  const base = budgetDefaultsFromDetail(detail);

  return {
    ...base,
    ...pickSummaryOverlay(summary),
    budgetServices: detail.budgetServices ?? base.budgetServices,
    generalInfo: detail.generalInfo ?? base.generalInfo,
    itemTypeList: detail.itemTypeList ?? base.itemTypeList,
    addedItemTypeList: detail.addedItemTypeList ?? base.addedItemTypeList,
    supplierList: detail.supplierList ?? base.supplierList,
    budgetGroups: detail.budgetGroups ?? base.budgetGroups,
    paymentSolicitation: detail.paymentSolicitation ?? base.paymentSolicitation,
    files: detail.files ?? base.files,
    baseValues: detail.baseValues ?? base.baseValues,
    mainAppliedTaxes: detail.mainAppliedTaxes ?? base.mainAppliedTaxes,
    budgetWideAppliedTaxes:
      detail.budgetWideAppliedTaxes ?? base.budgetWideAppliedTaxes,
    isApiUpdate: detail.isApiUpdate ?? base.isApiUpdate,
  };
}

/**
 * Updates list/sidebar summary fields from a full budget after save without
 * storing `budgetServices` in the list cache.
 */
export function patchSummaryFromBudget(
  summary: BudgetSummary,
  budget: Budget
): BudgetSummary {
  const next: BudgetSummary = {
    ...summary,
    _id: summary._id,
    project: budget.project,
    name: budget.name,
    status: budget.status,
    type: budget.type,
    budgetIndex: budget.budgetIndex,
    negotiationVersion: budget.negotiationVersion,
    versionHistory: budget.versionHistory,
    budgetValuesTotal: budget.budgetValuesTotal,
    negociationValuesTotal: budget.negociationValuesTotal,
    closingValuesTotal: budget.closingValuesTotal,
    currentVersionStatus: getBudgetCurrentStatus(budget.versionHistory),
  };

  if (typeof budget.totalsVersion === "number") {
    next.totalsVersion = budget.totalsVersion;
  }

  if (budget.budgetServices) {
    next.serviceCount = budget.budgetServices.length;
  }

  return next;
}

/** List row union: full document (v1) or summary DTO (v2 flag on). */
export type BudgetListItem = Budget | BudgetSummary;

/**
 * True when the list row is a full editor document (has `budgetServices`).
 * Summary DTOs omit that key entirely.
 */
export function isFullBudget(item: BudgetListItem): item is Budget {
  return "budgetServices" in item && Array.isArray(item.budgetServices);
}

/** Projects a full {@link Budget} into a list/sidebar {@link BudgetSummary}. */
export function budgetToSummary(budget: Budget): BudgetSummary {
  return patchSummaryFromBudget(
    {
      _id: budget._id ?? "",
      project: budget.project,
      name: budget.name,
      status: budget.status,
      type: budget.type,
      budgetIndex: budget.budgetIndex,
      negotiationVersion: budget.negotiationVersion,
      versionHistory: budget.versionHistory,
      budgetValuesTotal: budget.budgetValuesTotal,
      negociationValuesTotal: budget.negociationValuesTotal,
      closingValuesTotal: budget.closingValuesTotal,
    },
    budget
  );
}

/**
 * Value to store in `budgetList` after a save/create.
 * When v2 list mode is on, keeps summary fields only (no `budgetServices`).
 */
export function toBudgetListItem(
  budget: Budget,
  previous: BudgetListItem | undefined,
  useV2BudgetList: boolean
): BudgetListItem {
  if (!useV2BudgetList) return budget;
  if (previous && !isFullBudget(previous)) {
    return patchSummaryFromBudget(previous, budget);
  }
  return budgetToSummary(budget);
}

/**
 * Sidebar list row view for expanded accordion categories (FE-P1-07).
 *
 * - Matching `activeBudget._id`: return the hydrated editor doc so live
 *   totals / expanded categories mirror the reducer (D-01).
 * - Non-matching row under v2 list: always a summary (no `budgetServices`),
 *   even if a full doc somehow landed in list state.
 * - Non-matching row under v1 list: return the row unchanged.
 *
 * Callers should keep collapsed totals on the raw list row (autosave
 * snapshots) and only use this merge while the accordion is expanded.
 */
export function budgetForRow(
  row: BudgetListItem,
  activeBudget: Budget | undefined,
  useV2BudgetList: boolean
): BudgetListItem {
  if (row?._id && activeBudget?._id && row._id === activeBudget._id) {
    return activeBudget;
  }
  if (useV2BudgetList && isFullBudget(row)) {
    return budgetToSummary(row);
  }
  return row;
}
